import React, { useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import { getServicePackagePrices } from 'src/features/servicePackagePriceSlice';
import { getListAgencies } from 'src/features/agencySlice';

const formatCurrency = amount => {
  if (amount === null || amount === undefined || amount === '') return '-';
  return Number(amount).toLocaleString('vi-VN') + ' ₫';
};

export default function ViewAgencyPricesModal({ open, onClose, servicePackage }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const listServicePackagePrices = useSelector(
    state => state.servicePackagePriceSlice.listServicePackagePrices
  );
  const loadingPrices = useSelector(
    state => state.servicePackagePriceSlice.loading
  );
  const listAgencies = useSelector(
    state => state.agencySlice.listAgencies
  );
  const loadingAgencies = useSelector(
    state => state.agencySlice.isLoading
  );

  useEffect(() => {
    if (open && servicePackage?.id) {
      dispatch(
        getServicePackagePrices({
          service_package_id: servicePackage.id,
          page: 1,
          page_size: 1000
        })
      );
      // Fetch agencies list if it's empty to resolve agency names
      if (!listAgencies || listAgencies.length === 0) {
        dispatch(getListAgencies({ page_size: 1000 }));
      }
    }
  }, [open, servicePackage, dispatch]);

  // Client-side filtering as a fallback/safeguard
  const filteredPrices = (listServicePackagePrices || []).filter(item => {
    const pkg = item.service_package || item.servicePackage || item;
    const pkgId = pkg?.id || item.service_package_id;
    return Number(pkgId) === Number(servicePackage?.id);
  });

  const isLoading = loadingPrices || (loadingAgencies && (!listAgencies || listAgencies.length === 0));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6" style={{ fontWeight: 700, color: '#fff' }}>
          Giá đại lý đang bán: {servicePackage?.name || ''}
        </Typography>
        <IconButton className={classes.closeBtn} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={classes.content}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress size={36} style={{ color: '#D9042F' }} />
          </Box>
        ) : filteredPrices.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography variant="body1" color="textSecondary" style={{ fontWeight: 500 }}>
              Gói dịch vụ này hiện chưa được cấu hình giá riêng cho đại lý nào.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Tất cả đại lý sẽ mua với giá gốc mặc định: <b style={{ color: '#D9042F' }}>{formatCurrency(servicePackage?.cost)}</b>
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gridGap={10}>
              <Typography variant="body2" color="textSecondary">
                Giá gốc hệ thống: <b style={{ color: '#333' }}>{formatCurrency(servicePackage?.cost)}</b>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Tổng số đại lý áp dụng cấu hình: <b style={{ color: '#D9042F' }}>{filteredPrices.length} đại lý</b>
              </Typography>
            </Box>
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Table size="medium">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>Tên đại lý</TableCell>
                    <TableCell style={{ fontWeight: 600 }} align="right">Giá gốc hệ thống</TableCell>
                    <TableCell style={{ fontWeight: 600, color: '#D9042F' }} align="right">Giá bán cho đại lý</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPrices.map(item => {
                    const price = item.price ?? servicePackage?.cost;
                    // Resolve agency name
                    const agencyData = item.agency || item.owner;
                    const resolvedAgency = (listAgencies || []).find(
                      a => Number(a.id) === Number(item.owner_id)
                    );
                    const agencyName = agencyData?.name || resolvedAgency?.name || `Đại lý ID ${item.owner_id}`;

                    return (
                      <TableRow key={item.id} hover>
                        <TableCell style={{ fontWeight: 500 }}>{agencyName}</TableCell>
                        <TableCell align="right" style={{ color: '#777' }}>
                          {formatCurrency(servicePackage?.cost)}
                        </TableCell>
                        <TableCell align="right" style={{ fontWeight: 700, color: '#D9042F' }}>
                          {formatCurrency(price)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={onClose} variant="outlined" style={{ textTransform: 'none', color: '#555', borderColor: '#ccc' }}>
          Đóng
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#D9042F',
    color: '#fff'
  },
  closeBtn: { color: '#fff' },
  content: { padding: '24px' },
  tableHead: {
    backgroundColor: '#f5f5f5'
  }
}));
